// src/SidebarPrompt.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './DMCAApp.css';

function SidebarPrompt({
  user,
  chatHistory,
  isHistoryLoading,
  currentSessionId,
  usageStats,
  planTracker,
  onNewChat,
  onLoadSession,
  onDeleteSession,
  onLogout,
  getRelativeTime
}) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [expanded, setExpanded] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);

  if (!user) return null;

  // FIX Bug 5: don't divide by Infinity for Unlimited plan — always show full bar
  const usageBarPercent = () => {
    if (!usageStats || planTracker?.planConfig?.limits?.isUnlimited) return 100;
    const limit = planTracker.planConfig.limits.qrPerMonth;
    if (!limit) return 0;
    return Math.min((usageStats.qrGenerated / limit) * 100, 100);
  };

  // FIX: handle logout internally — signs out then redirects to landing page
  const handleLogout = async () => {
    try {
      await logout();
      setShowDropdown(false);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <div className={`sidebar ${expanded ? 'expanded' : 'collapsed'}`}>
      <div className="sidebar-content">

        {/* TOP */}
        <div className="sidebar-top">
          <div className="sidebar-header">
            <div className="sidebar-logo">
              <img src="/headerdark.png" alt="Logo" className="logo-image-sidebar" />
              {expanded && <h1 className="logo-text-sidebar">Claimzer.ai</h1>}
            </div>
            <button className="sidebar-toggle" onClick={() => setExpanded(!expanded)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M3 12H21M3 6H21M3 18H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          {expanded && (
            <>
              <button className="new-chat-btn" onClick={onNewChat}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span>New Notice</span>
              </button>

              <div className="chat-history">
                <div className="chat-history-header">Recent Notices</div>

                {isHistoryLoading ? (
                  <div className="history-loading">
                    <div className="spinner-small"></div>
                    <span>Loading...</span>
                  </div>
                ) : chatHistory.length > 0 ? (
                  chatHistory.map(chat => (
                    <div
                      key={chat.id}
                      className={`chat-item ${currentSessionId === chat.id ? 'active' : ''}`}
                      onClick={() => onLoadSession(chat)}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <div className="chat-item-content">
                        <div className="chat-item-title">{chat.title}</div>
                        <div className="chat-item-time">{getRelativeTime(chat.timestamp)}</div>
                      </div>
                      <button
                        className="delete-session-btn"
                        onClick={(e) => { e.stopPropagation(); onDeleteSession(chat.id, e); }}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="empty-history">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p>No notices yet</p>
                    <span>Your notices will appear here</span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* BOTTOM */}
        <div className="sidebar-bottom">
          {expanded ? (
            <>
              {usageStats && !planTracker?.loading && (
                <div className="sidebar-usage-card">
                  <div className="sidebar-usage-header">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <span>{planTracker.planConfig.name}</span>
                  </div>
                  <div className="sidebar-usage-stats">
                    <div className="sidebar-usage-item">
                      <span className="sidebar-usage-label">Used</span>
                      <span className="sidebar-usage-value">{usageStats.qrGenerated}</span>
                    </div>
                    <div className="sidebar-usage-item">
                      <span className="sidebar-usage-label">Left</span>
                      <span className="sidebar-usage-value">{usageStats.qrRemaining}</span>
                    </div>
                  </div>
                  <div className="sidebar-usage-progress">
                    {/* FIX Bug 5: use computed percent, not raw division that breaks on Infinity */}
                    <div
                      className="sidebar-usage-progress-bar"
                      style={{ width: `${usageBarPercent()}%` }}
                    />
                  </div>
                  <button className="sidebar-upgrade-btn" onClick={() => navigate('/pricing-plans')}>
                    Upgrade Plan
                  </button>
                </div>
              )}

              <div className="profile-dropdown-container">
                <button
                  className="sidebar-menu-item user-profile"
                  onClick={() => setShowDropdown(!showDropdown)}
                >
                  <div className="user-avatar">
                    <span className="user-initials">
                      {user.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="user-info">
                    <div className="user-name">{user?.displayName || user?.email?.split('@')[0]}</div>
                    <div className="user-email">{user?.email}</div>
                  </div>
                  <svg className="user-dropdown" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>

                {showDropdown && (
                  <div className="profile-dropdown-menu">
                    {/* FIX: use internal handleLogout instead of onLogout prop */}
                    <button className="profile-dropdown-item logout" onClick={handleLogout}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4m7 14l5-5-5-5m5 5H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Log Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <button className="sidebar-icon-btn" onClick={() => setExpanded(true)}>
              <span className="user-initials-small">{user.email.charAt(0).toUpperCase()}</span>
            </button>
          )}
        </div>

      </div>
    </div>
  );
}

export default SidebarPrompt;